import { Row, Col, Button, UncontrolledTooltip } from 'reactstrap';
import { IngestState } from '../../../features/ingest/ingestSlice';
import DropArea from '../../../common/DropArea';
import PickList from '../PickList';
import IngestActions from '../../../features/ingest/ingestActions';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import IngestGroup from '../../../common/domain/IngestGroup';
import { IngestFileType } from '../../../common/domain/IngestFileType';
import FileFormatLink from '../../../common/domain/FileFormatLink';
import ButtonsRow from './ButtonsRow';
import { withRouter } from 'react-router-dom';
import { getAuthState } from '../../../common/authentication';
import { env } from '../../../config';

const environment = env === 'development' ? 'DEV' : env.toUpperCase();
const CLIENTS_WITH_LE_MAPPING: Record<string, string> = {
  UOB: `TCA_${environment}_DATA_UOB`,
};

function getLEMappingClient(userGroups: string[]): string | null {
  if (userGroups.length === 0) return null;

  const clients = Object.keys(CLIENTS_WITH_LE_MAPPING).sort(
    (a, b) => b.length - a.length,
  );

  return (
    clients.find((client) =>
      userGroups.every((group) => group.includes(client)),
    ) ?? null
  );
}

export default withRouter((props: IngestState) => {
  const dispatch = useDispatch();
  const actions = useMemo(() => new IngestActions(dispatch), [dispatch]);

  const userGroups = getAuthState()?.userGroups;
  const leMappingClient = getLEMappingClient(userGroups ?? []);
  const isLEMappingEnabled = leMappingClient != null;
  const defaultGroupName = leMappingClient
    ? CLIENTS_WITH_LE_MAPPING[leMappingClient]
    : null;

  useEffect(() => {
    actions.loadFileTypes();
    actions.loadGroups();
    actions.loadFileFormats();
  }, [actions]);

  useEffect(() => {
    if (!defaultGroupName) return;
    actions.selectGroup({ id: defaultGroupName, name: defaultGroupName });
  }, [actions, defaultGroupName]);

  return (
    <>
      <Row className="justify-content-sm-start ml-2" />
      <Row className="justify-content-sm-start ml-2">
        <PickList<IngestFileType>
          {...{
            state: props.fileTypeSelector,
            defaultText: 'Select File Type',
            selectedText: (item) => `File Type: ${item.name}`,
            onItemSelected: (type) => actions.selectFileType(type),
          }}
        />
        {props.groupSelector.isLoading ||
        props.groupSelector.error ||
        !isLEMappingEnabled ? (
          <PickList<IngestGroup>
            {...{
              state: props.groupSelector,
              defaultText: 'Select Upload Group',
              selectedText: (item) => `Upload Group: ${item.name}`,
              onItemSelected: (group) => actions.selectGroup(group),
            }}
          />
        ) : (
          <>
            <span id="upload-group-tooltip">
              <Button
                className="mr-2"
                disabled
                style={{ pointerEvents: 'none' }}
              >
                Upload Group: {defaultGroupName}
              </Button>
            </span>
            <UncontrolledTooltip target="upload-group-tooltip">
              Groups are automatically assigned for each trade based on Legal
              Entity mapping
            </UncontrolledTooltip>
          </>
        )}
        <PickList<FileFormatLink>
          {...{
            state: props.fileFormatSelector,
            defaultText: 'View Required File Format',
            color: 'default',
            outline: true,
            direction: 'right',
            onItemSelected: (link) => {
              props?.history.push(link.path);
            },
          }}
        />
      </Row>
      <DropArea
        onFileSelected={(file) => actions.selectUploadFile(file)}
        file={props.fileToUpload}
      />
      <ButtonsRow {...props} />
      <Row className="justify-content-end mb-2">
        <Col md="auto">
          <section>
            File type, upload group, and a csv file must be specified before
            submitting
          </section>
        </Col>
      </Row>
    </>
  );
});
